import type { ComponentChildren } from "preact";
import { useEffect } from "preact/hooks";
import { PopoverPanel, usePopover } from "./popover";

// Red, bold and dotted means it opens an explanation; a label that opens nothing is muted.
export const TERM = "-m-1 p-1 font-bold text-link underline decoration-dotted decoration-[1.5px] underline-offset-[3px] hover:text-link-hover";

// A glossary term: the label opens a popover with the term's title, what it means and, for a number, the sum it is made of.
export function Term({
	label,
	title,
	text,
	lines = [],
	class: className = "",
}: {
	label: ComponentChildren;
	title: string;
	text?: ComponentChildren;
	lines?: string[];
	class?: string;
}) {
	const popover = usePopover<HTMLButtonElement>();
	const sum = lines.filter(Boolean);
	const heading = `${popover.id}-title`;
	const described = [text && `${popover.id}-text`, sum.length > 0 && `${popover.id}-sum`].filter(Boolean).join(" ");

	// A dialog that opens takes focus; Escape (in usePopover) hands it back to the term.
	useEffect(() => {
		if (popover.open) popover.panel.current?.focus({ preventScroll: true });
	}, [popover.open, popover.panel]);
	return (
		<>
			<button
				ref={popover.trigger}
				type="button"
				class={`${TERM} ${className}`}
				aria-expanded={popover.open}
				aria-controls={popover.open ? popover.id : undefined}
				onClick={popover.toggle}
			>
				{label}
			</button>
			{popover.open && (
				<PopoverPanel popover={popover} labelledBy={heading} describedBy={described || undefined} class="w-72 p-3 text-[14.5px] leading-snug">
					<h4 id={heading} class="cs-title text-[15px] font-bold">
						{title}
					</h4>
					{text && <div id={`${popover.id}-text`}>{text}</div>}
					{sum.length > 0 && (
						<div id={`${popover.id}-sum`} class="cs-num mt-1.5 border-t border-line pt-1.5 text-[15px]">
							{sum.map((line) => (
								<p key={line}>{line}</p>
							))}
						</div>
					)}
				</PopoverPanel>
			)}
		</>
	);
}
